import { useDirect130Host } from "@/app/_module/utils/direct130HostContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { isNativePlatform } from "@/lib/capacitor/platform";
import { canRingNatively } from "@/lib/capacitor/device-registration";
import { User as UserIcon, Building2, Phone, Bot, Camera, Loader2, MessageSquare, X, ShieldCheck, ClipboardCheck, BadgeCheck, Copy, Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import axiosInstance from "@/app/_module/services/axiosConfig";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import type { z } from "zod";

import { Avatar, AvatarImage, AvatarFallback } from "@/app/_module/components/ui/avatar";

import FormInput from "@/app/_module/components/FormInput";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/app/_module/components/ui/tooltip";
import { PhoneInput } from "@/app/_module/components/PhoneInput";
import { AgentPhoneSelector } from "@/app/_module/components/AgentPhoneSelector";
import { Button } from "@/app/_module/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/app/_module/components/ui/card";
import { Form, FormField } from "@/app/_module/components/ui/form";
import { Switch } from "@/app/_module/components/ui/switch";
import { Label } from "@/app/_module/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useDefaultOutboundPhoneValidation } from "@/app/_module/hooks/usePhoneUtils";
import { getInitialsFromFullName } from "@/app/_module/utils/get-initials";
import { displayAgencyRole } from "@/app/_module/utils/agency-display";
import { extractPhoneNumber, formatPhoneNumber, toE164Format } from "@/app/_module/utils/formatters";
import { getApiUrl } from "@/app/_module/services/config";
import supabaseAuthService from "@/app/_module/services/supabaseAuthService";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";
import { useFeatureFlag } from "@/app/_module/hooks/useFeatureFlag";
import { techficientService } from "@/app/_module/services/techficientService";
import { ManageProfile } from "../../../../quote-enroll/_modules/components/ManageProfile/ManageProfile";

import { profileInformationSchema } from "../../schema/profile-information-schema";
import { updateUser } from "../../services/user";
import type { User } from "../../types/user-types";
import { isDemoMode } from "@/app/_module/utils/demoMode";

type TProfileInformation = z.infer<typeof profileInformationSchema>;

const ProfileInformation = function () {
  const { user, refetchUser } = useUserContext();
  const onDirect130 = useDirect130Host();
  const { branding } = useBranding();
  const { toast } = useToast();
  const defaultOutboundPhoneValidation = useDefaultOutboundPhoneValidation();
  const queryClient = useQueryClient();
  const router = useRouter();

  // Quote & Enroll producer profile — surfaced here only when the agent has the
  // feature enabled. Eligibility drives whether we show "Manage" (already set
  // up) vs "Set up" (not provisioned yet). Shares the ["techficient",
  // "eligibility"] cache with the Quote & Enroll page, so no extra fetch there.
  const { enabled: qeEnabled } = useFeatureFlag("quoteAndEnroll");
  const [manageProfileOpen, setManageProfileOpen] = useState(false);
  const { data: qeEligibility } = useQuery({
    queryKey: ["techficient", "eligibility"],
    queryFn: () => techficientService.getEligibility(),
    enabled: qeEnabled,
  });


  // Profile picture upload state
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(() => {
    // Initialize with user's profile image if available
    const userRecord = user?.user as unknown as Record<string, unknown>;
    return (userRecord?.profileImageUrl as string) || (userRecord?.profile_image_url as string) || null;
  });
  const [imageKey, setImageKey] = useState(0); // Force re-render on image change
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<TProfileInformation>({
    resolver: zodResolver(profileInformationSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      phone: "", // Personal contact phone
      title: "",
      defaultOutboundPhone: "", // Default Twilio number for calls/SMS
      callForwardingEnabled: false, // Call forwarding toggle
      smsForwardingEnabled: false, // SMS forwarding toggle
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = form;

  // ── Auto-save ──────────────────────────────────────────────────────────────
  // Persist profile edits ~1.2s after the user stops typing, so they don't have
  // to hit Save. Guards (so it can't loop, spam, or corrupt data):
  //   • EMAIL changes are excluded — those use the explicit verification flow.
  //   • Phone is only saved once it parses to a valid US number (skips mid-typing).
  //   • Name only saved when ≥2 chars.
  //   • Never the default outbound number: only picking one saves it. The form resets from
  //     the in-app profile, which can be older than the database (another tab, a Settings
  //     tab switch), and auto-saving it wrote that old number over a new pick.
  //   • Deduped against the last saved payload; never runs two saves at once.
  //   • Silent (no toast) — a small "Saved" indicator is the feedback. The
  //     explicit Save button still exists (and handles email).
  const autoSaveLastRef = useRef<string | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAutoSavingRef = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;
  const [autoSaved, setAutoSaved] = useState(false);
  // Native app that the backend can VoIP-push: no call-forwarding toggle —
  // forwarding would just steal the call from the app, and the /m layout
  // switches it off. A phone WITHOUT push keeps the switch: forwarding is what
  // rings it while the app is closed.
  const [isNativeApp, setIsNativeApp] = useState(false);
  useEffect(() => { setIsNativeApp(isNativePlatform() && canRingNatively()); }, []);

  const autoSaveProfile = async (values: TProfileInformation) => {
    const u = userRef.current?.user as { id?: string; email?: string } | undefined;
    if (!u?.id) return;
    // Skip email — handled by the explicit Supabase verification flow.
    if (values.email && values.email !== u.email) return;

    // Phone — only include once it's a valid US number (skip while mid-typing).
    let e164Phone = "";
    if (values.phone && values.phone.trim() !== "") {
      try {
        e164Phone = toE164Format(values.phone);
      } catch {
        return;
      }
    }

    const payload: Record<string, unknown> = {
      contactPhone: e164Phone,
      callForwardingEnabled: values.callForwardingEnabled,
      smsForwardingEnabled: values.smsForwardingEnabled,
    };
    if (values.name && values.name.trim().length >= 2) payload.name = values.name.trim();
    if (typeof values.title === "string") payload.title = values.title;

    const key = JSON.stringify(payload);
    if (key === autoSaveLastRef.current || isAutoSavingRef.current) return;
    isAutoSavingRef.current = true;
    try {
      const updated = await updateUser(u.id, payload, { silent: true });
      if (updated) {
        autoSaveLastRef.current = key;
        setAutoSaved(true);
        // Keep the Agency Dashboard's cached name/title in sync — the old Save
        // button used to own this; auto-save now handles it so nothing is lost
        // by removing the button.
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'agencies' &&
            query.queryKey[1] === 'agencyUsers',
        });
      }
    } catch {
      // Silent — the explicit Save button surfaces errors. Auto-save just retries
      // on the next change.
    } finally {
      isAutoSavingRef.current = false;
    }
  };

  // Arrived from a lead's "Request Review" while unlinked: scroll to the card
  // and glow so the agent knows exactly what to press.
  const [lpHighlight, setLpHighlight] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const q = new URLSearchParams(window.location.search);
    if (q.get('connect') !== 'licensedproducer') return;
    setLpHighlight(true);
    const t = window.setTimeout(() => {
      document.getElementById('licensedproducer-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    const off = window.setTimeout(() => setLpHighlight(false), 8000);
    return () => { window.clearTimeout(t); window.clearTimeout(off); };
  }, []);

  // Licensedproducer connect state: true once linked (from the popup result or
  // the backend check on mount). lpProfileUrl deep-links to their live listing.
  const [lpLinked, setLpLinked] = useState(false);
  const [lpProfileUrl, setLpProfileUrl] = useState<string | null>(null);
  const [lpUrlCopied, setLpUrlCopied] = useState(false);
  const lpPopupRef = useRef<Window | null>(null);
  // Trailing-slash-proof: a Render env value of "https://licensedproducer.com/"
  // would otherwise build "…com//api/…" and 404 on LP's router.
  const lpBase = (process.env.NEXT_PUBLIC_LICENSEDPRODUCER_URL || 'https://licensedproducer.com').replace(/\/+$/, '');

  const openLpPopup = () => {
    // DEMO SANDBOX: this is a real SSO handshake against licensedproducer.com that
    // auto-provisions a PUBLIC listing and wires a lead-sync key. On a pooled seat that
    // means a stranger publishes a live producer profile under the demo identity, and it
    // lives on another service that the seat reset cannot touch — so it would outlast the
    // session and greet every visitor after them.
    //
    // Gated here rather than on the buttons because both entry points (Connect and
    // Re-sync) call this, and a future third caller inherits the block for free.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Connecting Licensedproducer is disabled in the demo sandbox. Start a free trial to publish your verified producer listing.",
        }),
      );
      return;
    }

    const url = `${lpBase}/api/auth/sso/insuracentral/start?popup=1&from=crm`;
    const w = Math.min(880, window.screen?.availWidth || 880);
    const h = Math.min(840, (window.screen?.availHeight || 840) - 40);
    const left = Math.max(0, (window.screenX || 0) + ((window.outerWidth || w) - w) / 2);
    const top = Math.max(0, (window.screenY || 0) + ((window.outerHeight || h) - h) / 2);
    const pop = window.open(url, 'lp_connect', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
    if (!pop) { window.location.href = url; return; } // popup blocked → same-tab fallback
    lpPopupRef.current = pop;
    pop.focus();
  };

  useEffect(() => {
    // Persisted profile link from a previous connect on this browser.
    try {
      const saved = localStorage.getItem('lp_profile_url');
      if (saved) { setLpProfileUrl(saved); setLpLinked(true); }
    } catch { /* ignore */ }
    // Authoritative: does the backend have the Licensedproducer link? This can
    // both CONFIRM and REVOKE — a stale localStorage from a since-removed link
    // must not keep the card showing Connected forever.
    (async () => {
      try {
        const r = await axiosInstance.get('/api/auth/licensedproducer-status');
        const d = r?.data?.data || r?.data;
        if (d?.linked) {
          setLpLinked(true);
        } else if (d && d.linked === false) {
          setLpLinked(false);
          setLpProfileUrl(null);
          try { localStorage.removeItem('lp_profile_url'); } catch { /* ignore */ }
        }
      } catch { /* network hiccup — leave whatever we showed */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Licensedproducer connect popup → result comes back via postMessage (the
  // popup closes itself). Turn it into a proper toast instead of leaving the
  // agent guessing what happened.
  useEffect(() => {
    const lpOrigin = new URL(process.env.NEXT_PUBLIC_LICENSEDPRODUCER_URL || 'https://licensedproducer.com').origin;
    const onMsg = (ev: MessageEvent) => {
      if (ev.origin !== lpOrigin) return;
      const d = ev.data as { type?: string; qs?: string };
      if (!d || d.type !== 'lp-sso' || typeof d.qs !== 'string') return;
      const p = new URLSearchParams(d.qs);
      const status = p.get('sso');
      // The popup tries to close itself, but browsers sometimes refuse a
      // script-close after cross-origin navigation — the OPENER closing its own
      // popup is always allowed, so do it from here.
      try { lpPopupRef.current?.close(); } catch { /* ignore */ }
      if (status === 'ok') {
        setLpLinked(true);
        const slug = p.get('profile');
        if (slug) {
          const url = `${lpOrigin}/agents/${slug}`;
          setLpProfileUrl(url);
          try { localStorage.setItem('lp_profile_url', url); } catch { /* ignore */ }
        }
        toast({
          title: 'Licensedproducer connected',
          description: 'Your verified listing is live and quote requests will sync into this CRM automatically.',
        });
      } else {
        const reasons: Record<string, string> = {
          subscription_required: 'Licensedproducer is available on paid plans (Basic and up). Upgrade your subscription, then connect again.',
          mfa_required: 'Multi-factor authentication is required. Enable MFA in your security settings, then connect again.',
          unverified: 'Your email is not verified yet. Verify it, then connect again.',
          link_conflict: 'That email is already linked to a different InsuraCentral account.',
        };
        toast({
          title: 'Could not connect Licensedproducer',
          description: reasons[status || ''] || 'Something went wrong — please try again.',
          variant: 'destructive',
        });
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sub = form.watch((values) => {
      // Only auto-save after the user has actually edited (reset/initial load
      // leaves the form not-dirty, so this won't fire on mount). NOTE: isDirty is
      // also read during render below, which is what makes RHF actually compute
      // it — without that render-time read this guard is always false and
      // auto-save never fires.
      if (!form.formState.isDirty) return;
      setAutoSaved(false); // reset the "saved" indicator while a new edit is pending
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(() => {
        void autoSaveProfile(values as TProfileInformation);
      }, 1200);
    });
    return () => {
      sub.unsubscribe();
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    if (user?.user) {
      // Don't clobber an in-progress edit. Re-running reset() on EVERY `user`
      // reference change — e.g. a background refetch on window focus — wipes the
      // phone/name/title the user is currently editing before the 1200ms
      // debounced auto-save persists it. That's why editing "Personal Phone"
      // appeared to do nothing / revert. Only sync from the server while the
      // form is clean (initial load, or after the edit has been saved & reset).
      if (form.formState.isDirty) return;

      // Format the phone number for display if it exists - check multiple possible field names
      const userRecord = user.user as unknown as Record<string, unknown>;
      // Priority: contactPhone > contact_phone > phone (for backward compatibility)
      const phoneValue = (userRecord.contactPhone as string) || (userRecord.contact_phone as string) || (userRecord.phone as string) || "";
      const formattedPhone = phoneValue ? formatPhoneNumber(phoneValue) : "";
      // Check if call forwarding is enabled - check both camelCase and snake_case
      const callForwardingValue = userRecord.callForwardingEnabledAt || userRecord.call_forwarding_enabled_at;
      const isCallForwardingEnabled = !!callForwardingValue;

      // Check if SMS forwarding is enabled - check both camelCase and snake_case
      const smsForwardingValue = userRecord.isSmsForwardingEnabled || userRecord.is_sms_forwarding_enabled;
      const isSmsForwardingEnabled = !!smsForwardingValue;

      // Set profile image URL - check both camelCase and snake_case
      const imageUrl = (userRecord.profileImageUrl as string) || (userRecord.profile_image_url as string) || null;
      setProfileImageUrl(imageUrl);
      // Get default outbound phone - check both formats
      const defaultPhone = (userRecord.defaultOutboundPhone as string) || (userRecord.default_outbound_phone as string) || "";

      reset({
        name: user.user.name || "",
        email: user.user.email || "",
        phone: formattedPhone,
        title: (userRecord.title as string) || "",
        defaultOutboundPhone: defaultPhone,
        callForwardingEnabled: isCallForwardingEnabled,
        smsForwardingEnabled: isSmsForwardingEnabled,
      });
    }
  }, [user, reset]);

  const handleProfilePictureClick = (e: React.MouseEvent | React.TouchEvent) => {
    // Prevent double-firing on touch devices
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // DEMO SANDBOX: the seat is shared, so an uploaded photo would greet the NEXT visitor — and it would
    // land in real storage that the seat reset does not clear.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Changing the profile picture is disabled in the demo sandbox. Start a free trial to set up your real profile.",
        }),
      );
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a PNG, JPG, JPEG, GIF, or WebP image.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: "Profile picture must be less than 5MB.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploadingPicture(true);

      const formData = new FormData();
      formData.append('file', file);

      // Get Supabase authentication token
      let supabaseToken = await supabaseAuthService.getAccessToken();

      // If no token, try refreshing the session
      if (!supabaseToken) {
        const refreshedSession = await supabaseAuthService.refreshSession();
        if (refreshedSession?.access_token) {
          supabaseToken = refreshedSession.access_token;
        }
      }
      if (!supabaseToken) {

        toast({
          title: "Authentication Error",
          description: "Please refresh the page and try again.",
          variant: "destructive",
        });
        throw new Error('Authentication token not available. Please refresh the page and try again.');
      }

      const apiUrl = getApiUrl('/users/profile/picture');

      // Get agency ID for the request header
      const agencyId = AgencyTokenManager.getCurrentAgencyId();

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${supabaseToken}`,
      };

      // Add agency ID header if available
      if (agencyId) {
        headers['x-agency-id'] = agencyId;
      }
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        throw new Error(errorData.message || 'Failed to upload profile picture');
      }

      const result = await response.json();
      // Update profile image URL immediately
      const newImageUrl = result.data.profileImageUrl;
      setProfileImageUrl(newImageUrl);
      setImageKey(prev => prev + 1); // Force re-render of avatar

      // Update user context with new profile image
      if (typeof refetchUser === "function") {
        refetchUser((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            profileImageUrl: newImageUrl || undefined,
            profileImagePath: result.data.profileImagePath || undefined,
          },
        }));
      }

      // Invalidate all user-related query caches to update avatars everywhere
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["auth-profile"] });
      queryClient.invalidateQueries({ queryKey: ["user-profile"] });

      toast({
        title: "Profile Picture Updated",
        description: "Your profile picture has been uploaded successfully.",
      });
    } catch (error) {

      toast({
        title: "Upload Failed",
        description: "Failed to upload profile picture. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingPicture(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveProfilePicture = async () => {
    // DEMO SANDBOX: removing the seeded photo would leave the seat blank for every visitor after this one.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Changing the profile picture is disabled in the demo sandbox. Start a free trial to set up your real profile.",
        }),
      );
      return;
    }

    try {
      setIsUploadingPicture(true);
      const supabaseToken = await supabaseAuthService.getAccessToken();
      if (!supabaseToken) return;

      const apiUrl = getApiUrl('/users/profile/picture');
      const agencyId = AgencyTokenManager.getCurrentAgencyId();
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${supabaseToken}`,
      };
      if (agencyId) {
        headers['x-agency-id'] = agencyId;
      }

      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to remove profile picture');
      }

      setProfileImageUrl(null);
      setImageKey(prev => prev + 1);

      if (typeof refetchUser === "function") {
        refetchUser((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            profileImageUrl: undefined,
            profileImagePath: undefined,
          },
        }));
      }

      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });

      toast({
        title: "Profile Picture Removed",
        description: "Your profile picture has been removed.",
      });
    } catch (error) {
      toast({
        title: "Remove Failed",
        description: "Failed to remove profile picture. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingPicture(false);
    }
  };

  const applyBackendUser = (u: User) => {
    // Format phone number for display - check multiple possible field names
    const userRecord = u as {
      contactPhone?: string;
      contact_phone?: string;
      phone?: string;
      profileImageUrl?: string;
      profile_image_url?: string;
      profileImagePath?: string;
      profile_image_path?: string;
      defaultOutboundPhone?: string;
      default_outbound_phone?: string;
      callForwardingEnabledAt?: string;
      call_forwarding_enabled_at?: string;
      isSmsForwardingEnabled?: boolean;
      is_sms_forwarding_enabled?: boolean;
    };
    // Priority: contactPhone > contact_phone > phone (for backward compatibility)
    const phoneValue = userRecord.contactPhone || userRecord.contact_phone || userRecord.phone || "";
    const formattedPhone = phoneValue ? formatPhoneNumber(phoneValue) : "";

    // Update profile image
    const imageUrl = userRecord.profileImageUrl || userRecord.profile_image_url || null;
    const imagePath = userRecord.profileImagePath || userRecord.profile_image_path || null;
    setProfileImageUrl(imageUrl);

    // Increment image key to force re-render with new image
    if (imageUrl) {
      setImageKey(prev => prev + 1);
    }
    // Update context
    if (typeof refetchUser === "function") {
      refetchUser((prev) => {
        const updatedContext = {
          ...prev,
          user: {
            ...prev.user,
            name: u.name ?? "",
            email: u.email ?? "",
            contactPhone: phoneValue,
            phone: phoneValue,
            profileImageUrl: imageUrl || undefined,
            profileImagePath: imagePath || undefined,
            title: (u as any).title ?? prev.user.title,
            defaultOutboundPhone: userRecord.defaultOutboundPhone || userRecord.default_outbound_phone || prev.user.defaultOutboundPhone,
            callForwardingEnabledAt: userRecord.callForwardingEnabledAt || userRecord.call_forwarding_enabled_at || prev.user.callForwardingEnabledAt,
            isSmsForwardingEnabled: userRecord.isSmsForwardingEnabled ?? userRecord.is_sms_forwarding_enabled ?? prev.user.isSmsForwardingEnabled,
          },
        };
        return updatedContext;
      });
    } else {

    }

    // Update form with formatted phone for display
    const callForwardingValue = userRecord.callForwardingEnabledAt || userRecord.call_forwarding_enabled_at;
    const isCallForwardingEnabled = !!callForwardingValue;

    const smsForwardingValue = userRecord.isSmsForwardingEnabled || userRecord.is_sms_forwarding_enabled;
    const isSmsForwardingEnabled = !!smsForwardingValue;

    const defaultPhone = userRecord.defaultOutboundPhone || userRecord.default_outbound_phone || "";

    const newFormData = {
      name: u.name ?? "",
      email: u.email ?? "",
      phone: formattedPhone,
      title: (u as { title?: string | null }).title ?? "",
      defaultOutboundPhone: defaultPhone,
      callForwardingEnabled: isCallForwardingEnabled,
      smsForwardingEnabled: isSmsForwardingEnabled,
    };
    reset(newFormData);
  };

  const onSubmit = async (values: TProfileInformation) => {
    // DEMO SANDBOX: name/title/personal number belong to the pooled seat; an edit here persists for the
    // next visitor, and the auth trigger can write it back into Supabase metadata.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Editing your profile is disabled in the demo sandbox. Start a free trial to manage your real details.",
        }),
      );
      return;
    }

    if (!user?.user?.id) {
      toast({
        title: "User ID missing",
        description: "Cannot update profile without user ID.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Check if email has changed - handle separately with Supabase
      if (values.email !== user.user.email) {
        try {
          const { getSupabaseClient } = await import('@/app/_module/services/supabaseClient');
          const supabase = await getSupabaseClient();

          const { data, error } = await supabase.auth.updateUser({
            email: values.email,
          });

          if (error) {
            toast({
              title: "Email Change Failed",
              description: error.message,
              variant: "destructive",
            });
            return;
          }

          toast({
            title: "Verification Email Sent",
            description: "Please check both your current and new email addresses to confirm the change. You'll remain logged in with your current email until verified.",
          });

          // Don't update email in form yet - wait for confirmation
          // Reset email field to current value
          form.setValue('email', user.user.email);
          return;
        } catch (error) {
          toast({
            title: "Email Change Failed",
            description: "Failed to send verification email. Please try again.",
            variant: "destructive",
          });
          return;
        }
      }

      // Convert phone number to E.164 format for backend storage
      let e164Phone = "";
      if (values.phone && values.phone.trim() !== "") {
        try {
          e164Phone = toE164Format(values.phone);
        } catch (error) {

          toast({
            title: "Invalid Phone Number",
            description: "Please enter a valid US phone number",
            variant: "destructive",
          });
          return;
        }
      }

      // No defaultOutboundPhone: the picker saves it the moment it's chosen, and this form's
      // copy can be an older number (see the auto-save note).
      const updatePayload: Record<string, unknown> = {
        name: values.name,
        email: values.email,
        contactPhone: e164Phone,
        title: values.title,
        callForwardingEnabled: values.callForwardingEnabled,
        smsForwardingEnabled: values.smsForwardingEnabled,
      };

      const updated = await updateUser(user.user.id, updatePayload);
      if (updated) {
        // Fetch the latest profile from the backend to ensure we have the correct agency-specific title
        try {
          const { getCurrentUser } = await import("../../services/user");
          const latestProfile = await getCurrentUser();
          // Apply the latest backend user data to update the form and context
          applyBackendUser(latestProfile);
        } catch (error) {

          // Fallback to using the response from the update
          applyBackendUser(updated);
        }

        // Invalidate agency users cache so the updated title appears in Agency Dashboard
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'agencies' &&
            query.queryKey[1] === 'agencyUsers'
        });

        toast({
          title: "Profile Updated",
          description: "Your profile has been updated successfully.",
        });
      } else {

        // Fallback: keep current form values
        reset(values);
        toast({
          title: "Update Failed",
          description: "Profile update failed. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {

      toast({
        title: "Update Failed",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Identity shown in the premium header (name + agency role + verified chip).
  const headerRecord = user?.user as unknown as Record<string, unknown> | undefined;
  const headerNpn =
    (headerRecord?.npn as string) || (headerRecord?.npn as number)?.toString() || "";
  const headerTitle = (headerRecord?.title as string) || "";
  // Not on Direct130: the agency is the brand, so the line would just repeat it (owner 2026-11-01).
  const headerRole = user?.currentAgency && !onDirect130
    ? displayAgencyRole({ ...user, title: headerTitle as string | null })
    : "";
  const identityLine =
    headerRole || headerTitle || "Manage your personal information and contact details";

  // The icon has to follow what the line actually SAYS, not just sit there.
  // displayAgencyRole returns an agency-led string ("BATCAVE - Owner",
  // "BATCAVE - Owner (Sales Manager)") — a person glyph next to an agency name
  // reads as wrong. But it also returns "Independent Agent" for solo users, and
  // the fallbacks are a bare job title or a generic blurb, none of which name an
  // agency. So: building only when the line is genuinely about an agency.
  const identityIsAgency = Boolean(headerRole) && headerRole !== "Independent Agent";
  const IdentityIcon = identityIsAgency ? Building2 : UserIcon;

  // IMPORTANT: read isDirty during render so react-hook-form actually tracks it.
  // The auto-save watcher gates on form.formState.isDirty; RHF only computes that
  // flag if something subscribes to it here. Without this read it stays false and
  // nothing auto-saves.
  const isFormDirty = form.formState.isDirty;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="relative">
        {/* Subtle brand gradient wash for a premium identity hero. */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-primary/[0.06] via-primary/[0.02] to-transparent pointer-events-none"
          aria-hidden="true"
        />
        <div className="relative flex items-center gap-4">
          <button
            type="button"
            className="relative group cursor-pointer bg-transparent border-none p-0"
            onClick={handleProfilePictureClick}
            onTouchEnd={handleProfilePictureClick}
            aria-label="Change profile picture"
          >
            <Avatar key={`avatar-${imageKey}`} className="h-20 w-20 ring-2 ring-white shadow-md border border-gray-200">
              <AvatarImage
                key={`avatar-img-${imageKey}`}
                src={profileImageUrl || undefined}
                alt={user?.user?.name || "Profile picture"}
                className="object-cover"
                onError={(e) => {

                }}
                onLoad={() => {
                }}
              />
              <AvatarFallback className="text-lg bg-gray-100 text-primary">
                {getInitialsFromFullName(
                  user?.user?.name?.split(" ")[0] || "John",
                  user?.user?.name?.split(" ")[1] || "Doe"
                )}
              </AvatarFallback>
            </Avatar>
            {/* Camera icon badge - bottom right corner */}
            <div className="absolute -bottom-0.5 -right-0.5 bg-primary rounded-full p-1 border border-white shadow-sm group-hover:bg-primary/90 transition-colors">
              {isUploadingPicture ? (
                <Loader2 className="h-3 w-3 text-white animate-spin" />
              ) : (
                <Camera className="h-3 w-3 text-white" />
              )}
            </div>
            {/* Remove button - top right corner, only when image exists */}
            {profileImageUrl && !isUploadingPicture && (
              <div
                role="button"
                tabIndex={0}
                className="avatar-remove-btn absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5 border border-white shadow-sm hover:bg-red-600 transition-colors cursor-pointer z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveProfilePicture();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    handleRemoveProfilePicture();
                  }
                }}
                aria-label="Remove profile picture"
              >
                <X className="h-2.5 w-2.5 text-white" />
              </div>
            )}
          </button>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
            onChange={handleFileChange}
            className="hidden"
            disabled={isUploadingPicture}
          />
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-xl truncate">
                {user?.user?.name || "Your profile"}
              </CardTitle>
              {headerNpn && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                  <ShieldCheck className="h-3 w-3" />
                  Verified
                </span>
              )}
            </div>
            <CardDescription className="flex items-center gap-1.5 truncate">
              <IdentityIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
              <span className="truncate">{identityLine}</span>
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <Form {...form}>
          <form
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            onSubmit={handleSubmit(onSubmit)}
          >
            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                // Name is locked here — changing it must go through support so it
                // stays in sync with A2P/compliance records. Hover explains why.
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="space-y-2 cursor-not-allowed">
                        <FormInput
                          required
                          disabled
                          containerClass="space-y-2 pointer-events-none"
                          error={errors.name}
                          id="lastname"
                          label="Name"
                          placeholder="Doe"
                          {...field}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-center">
                      Your name is locked. To change it, contact support at{" "}
                      <span className="font-medium">{branding.supportEmail}</span>.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            />

            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <div
                  className="space-y-2 cursor-pointer"
                  onClick={() => router.replace('/settings?tab=account&accountTab=security', { scroll: false })}
                >
                  <FormInput
                    disabled
                    containerClass="space-y-2 pointer-events-none"
                    error={errors.email}
                    id="email"
                    label="Email"
                    placeholder="john.doe@example.com"
                    type="email"
                    {...field}
                  />
                </div>
              )}
            />

            {/*
              DEMO SANDBOX: the personal number is not editable at all.
              Blocking on submit alone let a visitor type a new number, see it sit in the
              field, and only learn it was refused on save — which reads as the form being
              broken. Intercepting in the CAPTURE phase stops focus before the input ever
              receives it, so the prompt is the first thing that happens. A wrapper rather
              than props on PhoneInput: that component has a strict prop interface and is
              shared, so it should not grow demo-awareness for one caller.
            */}
            <div
              onMouseDownCapture={(e) => {
                if (!isDemoMode()) return;
                e.preventDefault();
                e.stopPropagation();
                (e.target as HTMLElement)?.blur?.();
                window.dispatchEvent(
                  new CustomEvent('demo-restricted', {
                    detail:
                      "Changing your personal phone number is disabled in the demo sandbox. Start a free trial to set up your real profile.",
                  }),
                );
              }}
              onKeyDownCapture={(e) => {
                // Keyboard reaches the field too (tab into it, then type).
                if (!isDemoMode()) return;
                if (e.key === 'Tab') return; // let focus move past it
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <FormField
                control={control}
                name="phone"
                render={({ field }) => (
                  <PhoneInput
                    required
                    containerClass="space-y-2"
                    error={errors.phone}
                    id="phone"
                    label="Personal Phone"
                    placeholder="(555) 123-4567"
                    value={field.value || ""}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            {/*
              DEMO SANDBOX: Title is not editable. Same capture-phase block as the personal
              phone above — stop focus before the input receives it, so the trial prompt is
              the first thing that happens rather than a refusal after typing.
            */}
            <div
              onMouseDownCapture={(e) => {
                if (!isDemoMode()) return;
                e.preventDefault();
                e.stopPropagation();
                (e.target as HTMLElement)?.blur?.();
                window.dispatchEvent(
                  new CustomEvent('demo-restricted', {
                    detail:
                      "Editing your title is disabled in the demo sandbox. Start a free trial to set up your real profile.",
                  }),
                );
              }}
              onKeyDownCapture={(e) => {
                if (!isDemoMode()) return;
                if (e.key === 'Tab') return;
                e.preventDefault();
                e.stopPropagation();
              }}
            >
            <FormField
              control={control}
              name="title"
              render={({ field }) => (
                <FormInput
                  containerClass="space-y-2"
                  error={errors.title}
                  id="title"
                  label="Title"
                  placeholder="Agent"
                  type="text"
                  {...field}
                  onBlur={() => {
                    field.onBlur();
                    // Persist the title when the user leaves the field (in addition
                    // to the debounced auto-save) so it never gets lost on a quick
                    // click-away or tab switch.
                    // The title auto-saves on blur, a WRITE PATH SEPARATE from onSubmit —
                    // gating submit alone would still have let an edit through here.
                    if (isDemoMode()) return;
                    const u = userRef.current?.user as { id?: string } | undefined;
                    if (u?.id && typeof field.value === "string") {
                      setAutoSaved(false);
                      updateUser(u.id, { title: field.value } as any, { silent: true })
                        .then(() => setAutoSaved(true))
                        .catch(() => { /* auto-save retries on next change */ });
                    }
                  }}
                />
              )}
            />
            </div>

            {/* Default Outbound Phone with Forwarding Dropdown */}
            <div className="space-y-2">
              <label htmlFor="defaultOutboundPhone" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Default Outbound Phone
              </label>
              <FormField
                control={control}
                name="defaultOutboundPhone"
                render={({ field }) => (
                  <AgentPhoneSelector
                    selectedPhoneNumber={field.value}
                    onPhoneNumberSelect={(phoneNumber) => {
                      // Only an agent's pick lands here (no auto-select), and it is the only
                      // thing that saves this number.
                      field.onChange(phoneNumber);
                      toast({
                        description: `Default outbound number: ${formatPhoneNumber(phoneNumber)}`,
                      });
                      const u = userRef.current?.user as { id?: string } | undefined;
                      if (u?.id && phoneNumber) {
                        setAutoSaved(false);
                        updateUser(u.id, { defaultOutboundPhone: phoneNumber } as any, { silent: true })
                          .then(() => {
                            setAutoSaved(true);
                            // The in-app profile kept the page-load number, so every reload of
                            // this form (switching Settings tabs) put the old one back.
                            if (typeof refetchUser === "function") {
                              refetchUser((prev) => ({ ...prev, user: { ...prev.user, defaultOutboundPhone: phoneNumber } }));
                            }
                          })
                          .catch(() => {
                            toast({ title: "Couldn't save your default number", description: "Please pick it again.", variant: "destructive" });
                          });
                      }
                    }}
                    // The form already shows the saved number (reset from the profile). Auto-select
                    // here re-saved the page-load number over a new pick: Anersi Koka's default kept
                    // snapping back to 817 (2026-10-09).
                    autoSelectDefault={false}
                    showAreaCode={false}
                    showStateInline={true}
                    placeholder="Select phone number"
                  />
                )}
              />

              {/* Forwarding Settings Dropdown */}
              <div className="p-2 border rounded-lg bg-gray-50 space-y-2">
                {/* Call Forwarding */}
                {isNativeApp && (
                  <p className="text-xs text-gray-500 flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 text-primary" />
                    Calls ring here in the app. Call forwarding is a desktop setting.
                  </p>
                )}
                {!isNativeApp && (
                <FormField
                  control={control}
                  name="callForwardingEnabled"
                  render={({ field }) => (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <Phone className="h-3.5 w-3.5 text-primary" />
                          <Label className="text-xs font-medium text-gray-700">
                            Forward Calls
                          </Label>
                        </div>
                        <Switch
                          checked={field.value || false}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            toast({
                              title: checked ? "Call Forwarding Enabled" : "Call Forwarding Disabled",
                              description: checked
                                ? "Incoming calls will ring both your phone and browser. First to answer wins. Outbound calls always use browser."
                                : "Call forwarding has been disabled. All calls will use browser-based WebRTC.",
                            });
                            // Persist the toggle immediately (discrete action).
                            const u = userRef.current?.user as { id?: string } | undefined;
                            if (u?.id) {
                              setAutoSaved(false);
                              updateUser(u.id, { callForwardingEnabled: checked } as any, { silent: true })
                                .then(() => setAutoSaved(true))
                                .catch(() => { /* auto-save retries on next change */ });
                            }
                          }}
                        />
                      </div>
                      {field.value && (
                        <p className="text-xs text-gray-500 mt-1 ml-6">
                          Incoming calls will ring both phone and browser (simultaneous ring). Answer on either device. Outbound calls always use browser for seamless dialing.
                        </p>
                      )}
                    </div>
                  )}
                />
                )}

                {/* SMS Forwarding */}
                <FormField
                  control={control}
                  name="smsForwardingEnabled"
                  render={({ field }) => (
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-3.5 w-3.5 text-green-600" />
                        <Label className="text-xs font-medium text-gray-700">
                          Forward SMS
                        </Label>
                      </div>
                      <Switch
                        checked={field.value || false}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          toast({
                            title: checked ? "SMS Forwarding Enabled" : "SMS Forwarding Disabled",
                            description: checked
                              ? "Incoming SMS will be forwarded to your personal phone. You can reply directly!"
                              : "SMS forwarding has been disabled",
                          });
                          // Persist the toggle immediately (discrete action).
                          const u = userRef.current?.user as { id?: string } | undefined;
                          if (u?.id) {
                            setAutoSaved(false);
                            updateUser(u.id, { smsForwardingEnabled: checked } as any, { silent: true })
                              .then(() => setAutoSaved(true))
                              .catch(() => { /* auto-save retries on next change */ });
                          }
                        }}
                      />
                    </div>
                  )}
                />
              </div>
            </div>

            {/* Right column: Current Agency + NPN stacked together, so this side
                fills the height of the taller Default Outbound Phone block on the
                left instead of leaving Agency at the top and NPN orphaned below. */}
            <div className="space-y-4 md:col-start-2 self-start">
              {/* Not on Direct130 (owner 2026-11-01): one agency, and it is the brand. */}
              {!onDirect130 && !!user?.currentAgency && (
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Current Agency
                  </label>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <Building2 className="h-4 w-4 text-primary" />
                    <span className="font-medium text-gray-900">
                      {displayAgencyRole({
                        ...user,
                        title: (user.user as unknown as Record<string, unknown>)?.title as string | null,
                      })}
                    </span>
                  </div>
                </div>
              )}

              {/* NPN — locked. Set during signup and verified against the state DOI;
                  unlockable only through support to prevent producer-number swaps. */}
              {(() => {
                const userRecord = user?.user as unknown as Record<string, unknown> | undefined;
                const npn = (userRecord?.npn as string) || (userRecord?.npn as number)?.toString() || "";
                const npnState = (userRecord?.npnState as string) || (userRecord?.npn_state as string) || "";
                return (
                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none">
                      NPN
                    </label>
                    <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                      <ShieldCheck className="h-4 w-4 text-primary" />
                      <span className="font-mono font-medium text-gray-900">
                        {npn || "Not set"}
                      </span>
                      {npnState && (
                        <span className="text-xs text-gray-500 uppercase tracking-wide">
                          · {npnState}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500">
                      {npn
                        ? "Verified at signup. Contact support to change your NPN."
                        : "Contact support to register your NPN."}
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* No Save button — every field persists automatically ~1.2s after
                you stop editing. This is just the status line. (Email is changed
                on the Security tab; pressing Enter in a field still forces a save.) */}
            <div className="md:col-span-2 flex items-center justify-end">
              <span className="text-sm flex items-center gap-1.5">
                {autoSaved ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-green-600"><polyline points="20 6 9 17 4 12" /></svg>
                    <span className="text-green-600">All changes saved</span>
                  </>
                ) : isFormDirty ? (
                  <span className="text-gray-500 flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Saving…
                  </span>
                ) : (
                  <span className="text-gray-400">Changes save automatically</span>
                )}
              </span>
            </div>
          </form>
        </Form>

        {/* Quote & Enroll producer profile — only when the feature is enabled. */}
        {qeEnabled && qeEligibility && (
          <div className="mt-2 pt-5 border-t border-gray-100 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <ClipboardCheck className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  Quote &amp; Enroll producer profile
                </p>
                <p className="text-xs text-gray-500">
                  {qeEligibility.provisioned
                    ? "Your state licenses and carrier writing numbers used for quoting."
                    : "Set up your producer profile to start quoting and enrolling."}
                </p>
              </div>
            </div>
            {qeEligibility.provisioned ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-shrink-0"
                onClick={() => setManageProfileOpen(true)}
              >
                Manage
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-shrink-0"
                onClick={() => router.push("/quote-enroll")}
              >
                Set up
              </Button>
            )}
          </div>
        )}

        {/* Licensedproducer — LIVE. One click starts "Sign in with InsuraCentral"
            on licensedproducer.com: the SSO auto-provisions their verified
            listing (all checks pass for paid IC subscribers) and wires the
            lead-sync key so quote requests land in this CRM, tagged. */}
        <div
          id="licensedproducer-card"
          className={`mt-2 rounded-xl border bg-gradient-to-br from-primary/[0.05] via-primary/[0.02] to-transparent p-4 transition-shadow ${lpHighlight ? 'border-emerald-500 ring-4 ring-emerald-500/25 shadow-lg' : 'border-primary/15'}`}
        >
          {lpHighlight && !lpLinked && (
            <p className="mb-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs font-medium text-emerald-800">
              Connect Licensedproducer here first — then head back to your lead to send the review invite.
            </p>
          )}
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <BadgeCheck className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                Licensedproducer
                {lpLinked ? (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                    <BadgeCheck className="h-3 w-3" /> Connected
                  </span>
                ) : (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                    New
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed max-w-xl">
                {lpLinked ? (
                  <>Your verified listing is <span className="font-medium text-gray-700">live on Licensedproducer</span>.
                  Quote requests from your listing sync straight into this CRM, tagged automatically.</>
                ) : (
                  <>Get your verified profile on <span className="font-medium text-gray-700">Licensedproducer</span> —
                  the registry where clients find licensed producers. Every quote request syncs
                  straight into this CRM, tagged automatically. One click, no re-signup:
                  you&apos;ll confirm your InsuraCentral login and your listing goes live instantly.</>
                )}
              </p>
              {lpLinked && lpProfileUrl && (
                <div className="mt-3 flex items-center gap-1.5 max-w-md rounded-lg border border-primary/20 bg-white/70 pl-3 pr-1.5 py-1.5">
                  <span className="text-xs font-mono text-gray-700 truncate flex-1" title={lpProfileUrl}>
                    {lpProfileUrl.replace(/^https?:\/\//, '')}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(lpProfileUrl).then(() => {
                        setLpUrlCopied(true);
                        setTimeout(() => setLpUrlCopied(false), 2000);
                      }).catch(() => {
                        toast({ title: 'Copy failed', description: 'Select and copy the link manually.', variant: 'destructive' });
                      });
                    }}
                    className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md transition-colors ${lpUrlCopied ? 'bg-emerald-100 text-emerald-700' : 'bg-primary/10 text-primary hover:bg-primary/20'}`}
                  >
                    {lpUrlCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {lpUrlCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {lpLinked ? (
                  <>
                    <a
                      href={lpProfileUrl || `${lpBase}/dashboard`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-primary text-white text-xs font-semibold px-3.5 py-2 rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <BadgeCheck className="h-3.5 w-3.5" />
                      {lpProfileUrl ? 'View My Public Profile' : 'Open My Dashboard'}
                    </a>
                    <button
                      type="button"
                      onClick={openLpPopup}
                      className="text-xs font-semibold text-gray-500 hover:text-gray-700 hover:underline px-2 py-2"
                    >
                      Re-sync
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={openLpPopup}
                    className="inline-flex items-center gap-1.5 bg-primary text-white text-xs font-semibold px-3.5 py-2 rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <BadgeCheck className="h-3.5 w-3.5" />
                    Connect Licensedproducer
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Manage producer profile dialog — same surface as the Quote & Enroll page. */}
        {qeEnabled && qeEligibility?.provisioned && (
          <Dialog open={manageProfileOpen} onOpenChange={setManageProfileOpen}>
            <DialogContent className="manage-profile-dialog sm:max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Manage producer profile</DialogTitle>
              </DialogHeader>
              <ManageProfile
                eligibility={qeEligibility}
                onClose={() => setManageProfileOpen(false)}
              />
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfileInformation;
